import React from "react";
import {
    useParams,
    useNavigate,
    useLocation,
} from "react-router-dom";

import {
    Box,
    Typography,
    Button,
    Breadcrumbs,
    Link,
} from "@mui/material";

import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

import moment from "moment";

import TypeIcon from "../../components/Reusables/HubSpokeIcon/TypeIcon";
import PatientJourney from "../../components/Reusables/PatientJourney";
import { formatPhone } from "../../components/Reusables/PatientJourney/caseDataAdapters";

import { useGetPeoples } from "../../api/settings";

/* =========================================================
   CONSTANTS
========================================================= */

const EARTH_RADIUS_KM = 6371;

/* =========================================================
   HELPERS
========================================================= */

const toRad = (deg) => (deg * Math.PI) / 180;

const getDistanceKm = (lat1, lng1, lat2, lng2) => {
    const φ1 = toRad(lat1);
    const φ2 = toRad(lat2);

    const Δφ = toRad(lat2 - lat1);
    const Δλ = toRad(lng2 - lng1);

    const a =
        Math.sin(Δφ / 2) ** 2 +
        Math.cos(φ1) *
        Math.cos(φ2) *
        Math.sin(Δλ / 2) ** 2;

    return (
        EARTH_RADIUS_KM *
        (2 *
            Math.atan2(
                Math.sqrt(a),
                Math.sqrt(1 - a)
            ))
    );
};

/* =========================================================
   PAGE
========================================================= */

export default function CaseDetails() {

    const navigate = useNavigate();

    const location = useLocation();

    /* -------------------------------------------------------
       CASE DATA
    ------------------------------------------------------- */

    const caseDt = location.state?.caseData;

    const caseData = caseDt;

    /* -------------------------------------------------------
       DOCTORS
    ------------------------------------------------------- */

    const {
        peoples = [],
        isLoading: doctorsLoading,
    } = useGetPeoples({
        role: "b2b-doctor",
        centerIds: [caseDt?.center_id],
    });

    /* -------------------------------------------------------
       CASE NOT FOUND
    ------------------------------------------------------- */

    if (!caseDt) {
        return (
            <Box
                p={6}
                textAlign="center"
                sx={{
                    fontFamily:
                        "Figtree, sans-serif",
                }}
            >
                <Typography
                    sx={{
                        fontFamily:
                            "Figtree, sans-serif",
                        fontSize: "14px",
                    }}
                >
                    No case data found
                </Typography>

                <Button
                    onClick={() => navigate(-1)}
                    sx={{
                        mt: 2,
                        textTransform: "none",
                        fontFamily:
                            "Figtree, sans-serif",
                    }}
                >
                    Go Back
                </Button>
            </Box>
        );
    }

    /* -------------------------------------------------------
       DISTANCE
    ------------------------------------------------------- */

    const distance =
        caseDt?.referred_by_center_lat &&
            caseDt?.referred_to_center_lat
            ? `${getDistanceKm(
                +caseDt.referred_by_center_lat,
                +caseDt.referred_by_center_lng,
                +caseDt.referred_to_center_lat,
                +caseDt.referred_to_center_lng
            ).toFixed(2)} km`
            : "—";

    /* =========================================================
       RENDER
    ========================================================= */

    return (
        <>
            <div className="main-wrappers">
                <div
                    className="page-wrapper"
                    style={{
                        maxHeight: "99vh",
                        overflowY: "auto",
                        paddingBottom: "40px",
                        paddingTop: "75px",
                        fontFamily: "Figtree",
                        backgroundColor:
                            "var(--app-bg)",
                    }}
                >
                    <div className="content">
                        <Box>
                            {/* =================================================
                                BREADCRUMBS
                            ================================================= */}

                            <Breadcrumbs
                                sx={{
                                    mb: 1.5,
                                }}
                            >
                                <Link
                                    underline="hover"
                                    onClick={() =>
                                        navigate("/cases")
                                    }
                                    sx={{
                                        cursor:
                                            "pointer",
                                        fontFamily:
                                            "Figtree, sans-serif",
                                        fontWeight: 500,
                                        fontSize:
                                            "14px",
                                        lineHeight:
                                            "1",
                                        letterSpacing:
                                            "-0.02em",
                                        color:
                                            "var(--app-text)",
                                    }}
                                >
                                    Referred Cases
                                </Link>

                                <Typography
                                    sx={{
                                        fontFamily:
                                            "Figtree, sans-serif",
                                        fontWeight: 600,
                                        fontSize:
                                            "14px",
                                        lineHeight:
                                            "1",
                                        letterSpacing:
                                            "-0.02em",
                                        color:
                                            "var(--app-text)",
                                    }}
                                >
                                    {caseDt.report_id}
                                </Typography>
                            </Breadcrumbs>

                            {/* =================================================
                                REPORT ID
                            ================================================= */}

                            <Typography
                                sx={{
                                    mb: {
                                        xs: 2,
                                        sm: 3,
                                    },
                                    fontFamily:
                                        "Figtree, sans-serif",
                                    fontWeight: 700,
                                    fontSize: {
                                        xs: "18px",
                                        sm: "20px",
                                    },
                                    lineHeight:
                                        "100%",
                                    letterSpacing:
                                        "-0.02em",
                                    color:
                                        "var(--app-text)",
                                }}
                            >
                                {caseDt.report_id}
                            </Typography>

                            {/* =================================================
                                CASE DETAILS
                            ================================================= */}

                            <Typography
                                sx={{
                                    mb: 1.5,
                                    fontFamily:
                                        "Figtree, sans-serif",
                                    fontWeight: 700,
                                    fontSize: {
                                        xs: "16px",
                                        sm: "18px",
                                    },
                                    lineHeight:
                                        "100%",
                                    letterSpacing:
                                        "-0.02em",
                                    color:
                                        "var(--app-text)",
                                }}
                            >
                                Case Details
                            </Typography>

                            <Box
                                sx={{
                                    bgcolor:
                                        "var(--app-border)",
                                    borderRadius: 2,
                                    p: {
                                        xs: 2,
                                        sm: 3,
                                    },
                                    mb: 3,
                                    display:
                                        "grid",
                                    gridTemplateColumns:
                                    {
                                        xs: "1fr",
                                        sm: "repeat(2, 1fr)",
                                        md: "repeat(4, 1fr)",
                                    },
                                    gap: {
                                        xs: 2,
                                        sm: 2.5,
                                        md: 3,
                                    },
                                }}
                            >
                                {/* REPORT ID */}

                                <Box>
                                    <Typography
                                        sx={{
                                            mb: 1.5,
                                            fontFamily:
                                                "Figtree, sans-serif",
                                            fontWeight: 800,
                                            fontSize:
                                                "12px",
                                            lineHeight:
                                                "100%",
                                            letterSpacing:
                                                "-0.02em",
                                            color:
                                                "var(--app-text-muted)",
                                        }}
                                    >
                                        REPORT ID
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontFamily:
                                                "Figtree, sans-serif",
                                            fontWeight: 700,
                                            fontSize:
                                                "15px",
                                            lineHeight:
                                                "100%",
                                            letterSpacing:
                                                "-0.02em",
                                            color:
                                                "var(--app-text)",
                                            textDecoration:
                                                "underline",
                                            cursor:
                                                "pointer",
                                        }}
                                        onClick={() =>
                                            navigate(
                                                `/ecg-reports/${caseDt.report_id}`
                                            )
                                        }
                                    >
                                        {
                                            caseDt.report_id
                                        }
                                    </Typography>
                                </Box>

                                {/* COLLECTION ID */}

                                <Box>
                                    <Typography
                                        sx={{
                                            mb: 1.5,
                                            fontFamily:
                                                "Figtree, sans-serif",
                                            fontWeight: 800,
                                            fontSize:
                                                "12px",
                                            lineHeight:
                                                "100%",
                                            letterSpacing:
                                                "-0.02em",
                                            color:
                                                "var(--app-text-muted)",
                                        }}
                                    >
                                        COLLECTION ID
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontFamily:
                                                "Figtree, sans-serif",
                                            fontWeight: 700,
                                            fontSize:
                                                "15px",
                                            lineHeight:
                                                "100%",
                                            letterSpacing:
                                                "-0.02em",
                                            color:
                                                "var(--app-text)",
                                        }}
                                    >
                                        {
                                            caseDt.collection_id ||
                                            "--"
                                        }
                                    </Typography>
                                </Box>

                                {/* PATIENT */}

                                <Box>
                                    <Typography
                                        sx={{
                                            mb: 1.5,
                                            fontFamily:
                                                "Figtree, sans-serif",
                                            fontWeight: 800,
                                            fontSize:
                                                "12px",
                                            lineHeight:
                                                "100%",
                                            letterSpacing:
                                                "-0.02em",
                                            color:
                                                "var(--app-text-muted)",
                                        }}
                                    >
                                        PATIENT
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontFamily:
                                                "Figtree, sans-serif",
                                            fontWeight: 700,
                                            fontSize:
                                                "15px",
                                            lineHeight:
                                                "100%",
                                            letterSpacing:
                                                "-0.02em",
                                            color:
                                                "var(--app-text)",
                                            textDecoration:
                                                "underline",
                                            cursor:
                                                "pointer",
                                        }}
                                        onClick={() =>
                                            navigate(
                                                `/patients/${caseDt.patient_user_id}`
                                            )
                                        }
                                    >
                                        {
                                            caseDt.patient_name ||
                                            "--"
                                        }
                                    </Typography>
                                </Box>

                                {/* REFERRED ON */}

                                <Box>
                                    <Typography
                                        sx={{
                                            mb: 1.5,
                                            fontFamily:
                                                "Figtree, sans-serif",
                                            fontWeight: 800,
                                            fontSize:
                                                "12px",
                                            lineHeight:
                                                "100%",
                                            letterSpacing:
                                                "-0.02em",
                                            color:
                                                "var(--app-text-muted)",
                                        }}
                                    >
                                        REFERRED ON
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontFamily:
                                                "Figtree, sans-serif",
                                            fontWeight: 700,
                                            fontSize:
                                                "15px",
                                            lineHeight:
                                                "100%",
                                            letterSpacing:
                                                "-0.02em",
                                            color:
                                                "var(--app-text)",
                                        }}
                                    >
                                        {caseDt.referred_on
                                            ? moment(
                                                caseDt.referred_on
                                            ).format(
                                                "DD/MM/YYYY, hh:mm a"
                                            )
                                            : "--"}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* =================================================
                                SPOKE AND HUB CENTRES
                            ================================================= */}

                            <Typography
                                sx={{
                                    mb: 1.5,
                                    fontFamily:
                                        "Figtree, sans-serif",
                                    fontWeight: 700,
                                    fontSize: {
                                        xs: "16px",
                                        sm: "18px",
                                    },
                                    lineHeight:
                                        "100%",
                                    letterSpacing:
                                        "-0.02em",
                                    color:
                                        "var(--app-text)",
                                }}
                            >
                                Spoke and Hub Centres
                            </Typography>

                            <Box
                                sx={{
                                    bgcolor:
                                        "var(--app-border)",
                                    borderRadius: 2,
                                    p: {
                                        xs: 2,
                                        sm: 3,
                                    },
                                    mb: 3,
                                }}
                            >
                                <Box
                                    sx={{
                                        display:
                                            "flex",
                                        alignItems:
                                            "center",
                                        justifyContent:
                                            "space-between",
                                        gap: {
                                            xs: 1.5,
                                            md: 3,
                                        },
                                        flexDirection:
                                        {
                                            xs: "column",
                                            md: "row",
                                        },
                                    }}
                                >
                                    {/* =================================================
                                        REFERRED FROM
                                    ================================================= */}

                                    <Box
                                        sx={{
                                            minWidth:
                                            {
                                                xs: "100%",
                                                md: 180,
                                            },
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                mb: 1,
                                                fontFamily:
                                                    "Figtree, sans-serif",
                                                fontWeight: 700,
                                                fontSize:
                                                    "12px",
                                                lineHeight:
                                                    "100%",
                                                letterSpacing:
                                                    "-0.02em",
                                                color:
                                                    "var(--app-text-muted)",
                                            }}
                                        >
                                            REFERRED FROM
                                        </Typography>

                                        <Typography
                                            sx={{
                                                mb: 2,
                                                display:
                                                    "flex",
                                                alignItems:
                                                    "center",
                                                gap: 0.5,
                                                fontFamily:
                                                    "Figtree, sans-serif",
                                                fontWeight: 600,
                                                fontSize:
                                                    "15px",
                                                lineHeight:
                                                    "100%",
                                                letterSpacing:
                                                    "-0.02em",
                                                color:
                                                    "var(--app-text)",
                                            }}
                                        >
                                            <TypeIcon
                                                type={
                                                    caseDt.referred_by_center_type
                                                }
                                                size={15}
                                            />

                                            {caseDt.referred_by_center_name ||
                                                "--"}
                                        </Typography>

                                        <Typography
                                            sx={{
                                                mb: 0.5,
                                                fontFamily:
                                                    "Figtree, sans-serif",
                                                fontWeight: 600,
                                                fontSize:
                                                    "13px",
                                                lineHeight:
                                                    "100%",
                                                letterSpacing:
                                                    "-0.02em",
                                                color:
                                                    "var(--app-text)",
                                            }}
                                        >
                                            {caseDt.referred_by_admin_name ||
                                                "--"}
                                        </Typography>

                                        <Typography
                                            sx={{
                                                fontFamily:
                                                    "Figtree, sans-serif",
                                                fontWeight: 400,
                                                fontSize:
                                                    "12px",
                                                lineHeight:
                                                    "100%",
                                                letterSpacing:
                                                    "-0.02em",
                                                color:
                                                    "var(--app-text)",
                                            }}
                                        >
                                            {formatPhone(caseDt.referred_by_admin_phone) ||
                                                "--"}
                                        </Typography>
                                    </Box>

                                    {/* =================================================
                                        ARROW
                                    ================================================= */}

                                    <Box
                                        sx={{
                                            flex: 1,
                                            mx: {
                                                xs: 0,
                                                md: 2,
                                            },
                                            width: {
                                                xs: "100%",
                                                md: "auto",
                                            },
                                            textAlign:
                                                "center",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                position:
                                                    "relative",
                                                borderBottom:
                                                    "2px dotted #5751E9",
                                                height: "1px",
                                            }}
                                        >
                                            <KeyboardArrowRightIcon
                                                sx={{
                                                    position:
                                                        "absolute",
                                                    right: "-10px",
                                                    bottom:
                                                        "-12px",
                                                    color:
                                                        "#5751E9",
                                                    transform:
                                                    {
                                                        xs: "rotate(90deg)",
                                                        md: "none",
                                                    },
                                                }}
                                            />
                                        </Box>

                                        <Typography
                                            sx={{
                                                mt: 1,
                                                fontFamily:
                                                    "Figtree, sans-serif",
                                                fontSize:
                                                    "10px",
                                                color:
                                                    "var(--app-text-muted)",
                                            }}
                                        >
                                            {distance}
                                        </Typography>
                                    </Box>

                                    {/* =================================================
                                        REFERRED TO
                                    ================================================= */}

                                    <Box
                                        sx={{
                                            minWidth:
                                            {
                                                xs: "100%",
                                                md: 180,
                                            },
                                            textAlign:
                                            {
                                                xs: "left",
                                                md: "right",
                                            },
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                mb: 1,
                                                fontFamily:
                                                    "Figtree, sans-serif",
                                                fontWeight: 700,
                                                fontSize:
                                                    "12px",
                                                lineHeight:
                                                    "100%",
                                                letterSpacing:
                                                    "-0.02em",
                                                color:
                                                    "var(--app-text-muted)",
                                            }}
                                        >
                                            REFERRED TO
                                        </Typography>

                                        <Typography
                                            sx={{
                                                mb: 2,
                                                display:
                                                    "flex",
                                                alignItems:
                                                    "center",
                                                justifyContent:
                                                {
                                                    xs: "flex-start",
                                                    md: "flex-end",
                                                },
                                                gap: 0.5,
                                                fontFamily:
                                                    "Figtree, sans-serif",
                                                fontWeight: 600,
                                                fontSize:
                                                    "15px",
                                                lineHeight:
                                                    "100%",
                                                letterSpacing:
                                                    "-0.02em",
                                                color:
                                                    "var(--app-text)",
                                            }}
                                        >
                                            <TypeIcon
                                                type={
                                                    caseDt.referred_to_center_type
                                                }
                                                size={15}
                                            />

                                            {caseDt.referred_to_center_name ||
                                                "--"}
                                        </Typography>

                                        <Typography
                                            sx={{
                                                mb: 0.5,
                                                fontFamily:
                                                    "Figtree, sans-serif",
                                                fontWeight: 600,
                                                fontSize:
                                                    "13px",
                                                lineHeight:
                                                    "100%",
                                                letterSpacing:
                                                    "-0.02em",
                                                color:
                                                    "var(--app-text)",
                                            }}
                                        >
                                            {caseDt.referred_to_admin_name ||
                                                "--"}
                                        </Typography>

                                        <Typography
                                            sx={{
                                                fontFamily:
                                                    "Figtree, sans-serif",
                                                fontWeight: 400,
                                                fontSize:
                                                    "12px",
                                                lineHeight:
                                                    "100%",
                                                letterSpacing:
                                                    "-0.02em",
                                                color:
                                                    "var(--app-text)",
                                            }}
                                        >
                                            {formatPhone(caseDt.referred_to_admin_phone) ||
                                                "--"}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            {/* =================================================
                                PATIENT JOURNEY / CONTACT / CARE TEAM / LOG
                            ================================================= */}

                            <PatientJourney
                                caseData={caseData}
                                reportId={caseData?.report_id}
                                peoples={peoples}
                                doctorsLoading={
                                    doctorsLoading
                                }
                            />
                        </Box>
                    </div>
                </div>
            </div>
        </>
    );
}